import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ExternalLink, FolderTree, Newspaper, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type RowAction,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { BlogPost } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { openStorefrontUrl } from '@/features/stores';
import { useStore } from '@/shared/store/store-context';

import { useBlogPostColumns } from '../components/blog-post-columns';
import { blogPostFilters } from '../components/blog-post-filters';
import { DeleteBlogPostDialog } from '../components/delete-blog-post-dialog';
import { useBlogPosts } from '../hooks/use-blog-posts';

const ROUTE_ID = '/_authed/_active/s/$storeId/content/blog/' as const;
const ROUTE_PATH = '/s/$storeId/content/blog/' as const;

function NewPostButton({ storeId, tour }: { storeId: string; tour?: string }) {
  const { t } = useTranslation('blog');
  return (
    <Can permission="content.write">
      <Button asChild data-tour={tour}>
        <Link to="/s/$storeId/content/blog/new" params={{ storeId }}>
          <Plus aria-hidden />
          {t('posts.new')}
        </Link>
      </Button>
    </Can>
  );
}

function BlogPosts() {
  const { t } = useTranslation('blog');
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can('content.write');
  const [deleting, setDeleting] = useState<BlogPost | null>(null);

  const filters = useMemo(
    () => blogPostFilters({ t, queryClient, storeId: store.id }),
    [t, queryClient, store.id],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useBlogPosts(store.id, list.params);

  const open = useCallback(
    (row: BlogPost) =>
      void navigate({
        to: '/s/$storeId/content/blog/$postId',
        params: { storeId: store.id, postId: row.id },
      }),
    [navigate, store.id],
  );

  const rowActions = useMemo<Array<RowAction<BlogPost>>>(
    () => [
      { id: 'open', label: t('actions.open'), icon: <Pencil aria-hidden />, onAction: open },
      {
        id: 'view',
        label: t('actions.view'),
        icon: <ExternalLink aria-hidden />,
        hidden: (row) => !row.storefrontUrl || !row.live,
        onAction: (row) => openStorefrontUrl(row.storefrontUrl),
      },
      {
        id: 'delete',
        label: t('actions.delete'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        hidden: () => !canWrite,
        onAction: (row) => setDeleting(row),
      },
    ],
    [canWrite, open, t],
  );

  const columns = useBlogPostColumns(store.id);
  const rows = query.data?.items ?? [];
  const table = useDataTable({
    mode: 'server',
    data: rows,
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'blog-posts',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && rows.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('posts.title')}
        description={t('posts.description', { store: store.name })}
        actions={
          <>
            <Button asChild variant="outline" data-tour="content.blog.categories-link">
              <Link to="/s/$storeId/content/blog/categories" params={{ storeId: store.id }}>
                <FolderTree aria-hidden />
                {t('posts.manageCategories')}
              </Link>
            </Button>
            <NewPostButton storeId={store.id} tour="content.blog.new" />
          </>
        }
      />
      {isEmptyList ? (
        <EmptyState
          icon={Newspaper}
          title={t('posts.empty.title')}
          description={t('posts.empty.description')}
          action={<NewPostButton storeId={store.id} />}
          tour="content.blog.empty"
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          tour={{ table: 'content.blog.table' }}
          search={{ placeholder: t('posts.search'), label: t('posts.search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          rowActions={rowActions}
          onRowClick={open}
          getRowLabel={(row) => row.title}
          resourceName={{ singular: t('posts.resource.one'), plural: t('posts.resource.other') }}
          emptyState={{ title: t('posts.noMatches') }}
        />
      )}
      <DeleteBlogPostDialog
        storeId={store.id}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function BlogPostsPage() {
  return (
    <FeatureErrorBoundary name="blog.posts">
      <BlogPosts />
    </FeatureErrorBoundary>
  );
}
